import { useMemo } from "react";

import { mergeCategoryFiltersConfigs } from "app/pages/sales-intelligence/modules/advanced-search-composition/utils/filterConfig";
import { allCategoryConfig } from "app/pages/sales-intelligence/modules/advanced-search-filtering/configs/filterCategory";
import type { IFiltersConfigCategoryItem } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/category";
import { filterUsageMap } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterDefinition";
import type { FiltersConfig } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import type {
    OrderedSearchFilterInstance,
    SearchFilterInstance,
} from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/searchFilterInstance";
import { createAppsFiltersSearchConfig } from "app/pages/sales-intelligence/modules/apps-search-filters/configs/createAppsFiltersSearchConfig";
import { useComputeAppsFiltersConfig } from "app/pages/sales-intelligence/modules/apps-search-filters/hooks/useComputeAppsFiltersConfig";
import { createContactsFiltersSearchConfig } from "app/pages/sales-intelligence/modules/contacts-search-filters/configs/createContactsFiltersSearchConfig";
import { useComputeContactsFiltersConfig } from "app/pages/sales-intelligence/modules/contacts-search-filters/hooks/useComputeContactsFiltersConfig";
import { useCrmCustomFiltersListViewModel } from "app/pages/sales-intelligence/modules/crm-integration/hooks/useCrmCustomFiltersListViewModel";
import { createLeadsFiltersSearchConfig } from "app/pages/sales-intelligence/modules/leads-search-filters/configs/createLeadsFiltersSearchConfig";
import { useComputeLeadsFiltersConfig } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useComputeLeadsFiltersConfig";
import { useCrmFilters } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useCrmFilters";
import { useLeadsGrants } from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useLeadsGrants";

type UseFiltersConfigResult = FiltersConfig<readonly SearchFilterInstance[]> & {
    readonly categoriesList: readonly IFiltersConfigCategoryItem[];
};

export const useFiltersConfig = (): UseFiltersConfigResult => {
    const crmCustomFiltersListViewModel = useCrmCustomFiltersListViewModel();

    const leadsGrants = useLeadsGrants({
        isExcludeSectionAvailable: true,
        isCrmScopeFilterAvailable: true,
        activeCrmIntegration: crmCustomFiltersListViewModel.integrationObject?.integrationName,
    });

    const leadsSelection = useMemo(() => ({ usage: filterUsageMap.search, grants: leadsGrants }), [leadsGrants]);

    const leadsResult = useComputeLeadsFiltersConfig({
        selection: leadsSelection,
        applySuppressionListByDefault: true,
    });
    const contactsResult = useComputeContactsFiltersConfig();
    const appsResult = useComputeAppsFiltersConfig();
    const crmResult = useCrmFilters(crmCustomFiltersListViewModel, leadsGrants);

    const mergedFiltersConfig = useMemo(
        () =>
            mergeCategoryFiltersConfigs<OrderedSearchFilterInstance>(
                allCategoryConfig,
                leadsResult,
                crmResult,
                contactsResult,
                appsResult,
            ),
        [
            leadsResult.status,
            leadsResult.filtersList,
            leadsResult.categoriesList,
            crmResult.status,
            crmResult.filtersList,
            crmResult.categoriesList,
            contactsResult.status,
            contactsResult.filtersList,
            contactsResult.categoriesList,
            appsResult.status,
            appsResult.filtersList,
            appsResult.categoriesList,
        ],
    );

    const filtersSearchConfigList = useMemo(
        () => [createLeadsFiltersSearchConfig(), createContactsFiltersSearchConfig(), createAppsFiltersSearchConfig()],
        [],
    );

    return {
        filtersSearchConfigList,
        ...mergedFiltersConfig,
    };
};
